/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import React, { useCallback, useContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useTranslator } from '@jutro/locale';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { useModal } from '@jutro/components';
import { ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { FormModalPopover } from 'ml-components-platform-react';
import _ from 'lodash';
import appConfig from 'app-config';
import '@xengage/gw-platform-translations';
import metadata from './MLHOEditMineSubSchedItemComponent.metadata.json5'
import messages from './MLHOEditMineSubSchedItemComponent.messages';
import styles from './MLHOEditMineSubSchedItemComponent.module.scss';
import mineSubSchedItemPopup from './MLHOMineSubSchedItemPopup.metadata.json5';

function MLHOEditMineSubSchedItemComponent(props) {
    const modalApi = useModal();
    const {
        properties,
        defaultValue,
        dtoPath,
        loading,
        onListChange,
        isAgent,
        isReadOnly
    } = props;

    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);

    const isQnb = appConfig.portalCode === 'QB';

    const formatOptionAvailableVals = useCallback((options) => {
        if (!options) return [];
        return options.map((opt) => ({
            code: opt.code,
            name: {
                id: opt.code,
                defaultMessage: opt.name
            }
        }));
    }, []);

    const yesNo = useMemo(() => {
        return viewModelService.productMetadata.get('pc').types.getTypelist('YesNo').filters.filter((n) => {
            return n.name === 'YesNoOnly';
        })[0].codes.map((type) => ({
            code: type.code,
            name: translator({ id: type.name, defaultMessage: type.name })
        }))
    }, [translator, viewModelService.productMetadata])

    const getResetDefault = (defaultVppObj) => {
        const copyOfVpp = _.cloneDeep(defaultVppObj);
        const keys = Object.keys(copyOfVpp);
        keys.forEach((k) => {
            // eslint-disable-next-line no-prototype-builtins
            if (copyOfVpp[k].hasOwnProperty('chosenTerm')) {
                copyOfVpp[k].chosenTerm = null;
                copyOfVpp[k].chosenTermValue = '';
            }
        });
        copyOfVpp.description = null;
        return _.omit(copyOfVpp, ['limit']);
    };

    const addItemHandler = useCallback((vm) => {
        if (vm.aspects.valid && vm.aspects.subtreeValid) {
            if (vm.value.limit) {
                vm.value.limit = parseFloat(vm.value.limit).toFixed(2);
            }
            properties.push(vm.value);
            onListChange();
            return true;
        }
        return false;
    }, [onListChange, properties]);

    const editItemHandler = useCallback((vm) => {
        const indexOfCurrentItem = properties.findIndex((e) => e.fixedId === vm.fixedId.value);
        if (vm.aspects.valid && vm.aspects.subtreeValid && indexOfCurrentItem !== -1) {
            if (vm.value.itemValue) {
                vm.value.itemValue = parseFloat(vm.value.itemValue).toFixed(2);
            }
            properties[indexOfCurrentItem] = vm.value;
            onListChange();
            return true;
        }
        return false;
    }, [onListChange, properties]);

    const getPopupProps = useCallback((vm, isEdit) => {
        const uiProps = mineSubSchedItemPopup.pageContent;
        return {
            title: isEdit ? translator(messages.editMineSubsidence) : translator(messages.add),
            uiProps,
            actionBtnLabel: messages.modalContinue,
            cancelBtnLabel: isAgent ? messages.modalCancel : undefined,
            viewModel: vm,
            getOverrideProps: () => {
                return {
                    deductibleDropdownSelect: {
                        value: vm?.deductible?.chosenTerm?.value,
                        availableValues: formatOptionAvailableVals(vm?.deductible?.options?.value),
                    },
                    isSchedPropOnInsuredPremisDropdownSelect: {
                        availableValues: yesNo,
                        value: vm?.isLocatedOnPrimaryInsuredPremise?.chosenTerm?.value,
                    },
                };
            },
            onSaveAction: isEdit ? editItemHandler : addItemHandler
        };
    }, [addItemHandler, editItemHandler, formatOptionAvailableVals, isAgent]);

    const onAddItem = useCallback(() => {
        const isEdit = false;
        const defaultVal = getResetDefault(defaultValue);
        const vm = viewModelService.create(defaultVal, 'pc', dtoPath);
        const componentProps = getPopupProps(vm, isEdit);

        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        );
    }, [defaultValue, dtoPath, getPopupProps, viewModelService]);

    const onEditItem = useCallback((evt) => {
        const isEdit = true;
        const selectedItem = properties[evt.index];
        const selectedItemVm = viewModelService.create(selectedItem, 'pc', dtoPath);
        const componentProps = getPopupProps(selectedItemVm, isEdit);

        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        );
    }, [dtoPath, getPopupProps, properties, viewModelService]);

    const onRemoveItem = useCallback((evt) => {
        properties.splice(evt.index, 1);
        onListChange();
    }, [onListChange, properties]);

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onEditItem,
            onRemoveItem,
            onAddItem
        }
    };

    const generateItemOverrides = useCallback(() => {
        const itemOverrides = properties.map((item, itemIndex) => {
            return {
                [`tableRowDiv${itemIndex}`]: {
                    visible: true,
                },
                [`actionRow${itemIndex}`]: {
                    visible: !isReadOnly,
                    className: loading ? 'cell disabled' : 'cell'
                },
            };
        });

        return Object.assign({}, ...itemOverrides);
    }, [isReadOnly, loading, properties]);

    const getButtonContent = () => {
        return translator(messages.add)
    };

    const overrides = {
        parentDiv: {
            className: !isReadOnly ? 'ho-edit-scheduled-property' : ''
        },
        tableRowIterable: {
            data: properties
        },
        addItemBtn: {
            visible: !isReadOnly,
            disabled: properties.length === 5,
            content: getButtonContent(),
            className: isQnb ? 'mineAddButtonSpacing' : ''
        },
        ...generateItemOverrides()
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            {...resolvers} />
    );
}

MLHOEditMineSubSchedItemComponent.propTypes = {
    properties: PropTypes.arrayOf(PropTypes.shape({})),
    defaultValue: PropTypes.shape({}),
    dtoPath: PropTypes.string,
    onListChange: PropTypes.func,
    loading: PropTypes.bool,
    isAgent: PropTypes.bool,
    isReadOnly: PropTypes.bool
};

export default MLHOEditMineSubSchedItemComponent;
